// components/EMT/Navigation/AppModeSelect.tsx
import React, { useMemo } from 'react'
import { Box } from '@mui/material'
import NextImage from 'next/image'
import { useRouter } from 'next/router'
import { EmtSelect, SelectOptionsType } from 'emt-components-v2'
import { openUrlApp } from '@/utils'

import { usePetsittingMode } from '@/store/usePetsittingMode'
import { Routes } from '@/assets/configs'
import VariantImages from '@/assets/VariantImages'
import { TokenKeys } from '@/modules/Auth/LocalStorageHelper'

const APP_MODE = {
  EMT: 'emt',
  SITTER: 'sitter',
} as const

type AppMode = (typeof APP_MODE)[keyof typeof APP_MODE]

type AppModeSelectProps = {
  appendQueryType: (href: any) => any
  sx?: any
}

export default function AppModeSelect({ appendQueryType, sx }: AppModeSelectProps) {
  const router = useRouter()
  const { isPetsittingMode } = usePetsittingMode()

  const appModeValue: AppMode = isPetsittingMode ? APP_MODE.SITTER : APP_MODE.EMT

  const appOptions: SelectOptionsType = useMemo(
    () => [
      {
        value: APP_MODE.EMT,
        label: 'Emprunte Mon Toutou',
        caption: 'Des bénévoles pour vos animaux',
        media: (
          <Box sx={{ width: 34, height: 34, position: 'relative' }}>
            <NextImage src={VariantImages.EmtSelect} alt="Emprunte Mon Toutou" fill style={{ objectFit: 'contain' }} />
          </Box>
        ),
      },
      {
        value: APP_MODE.SITTER,
        label: 'Super Sitters',
        caption: 'Pet sitters de confiance',
        media: (
          <Box sx={{ width: 34, height: 34, position: 'relative' }}>
            <NextImage src={VariantImages.SSSelect} alt="Super Sitters" fill style={{ objectFit: 'contain' }} />
          </Box>
        ),
      },
    ],
    [],
  )

  return (
    <EmtSelect<AppMode>
      sx={{
        '& .MuiSelect-select': { py: 1.1, pr: 5 },
        maxHeight: '57px',
        height: '57px',
      }}
      MenuProps={{
        sx: {
          '& .active': { background: '#77757F1A', color: '#1D1929', border: '1px solid #77757F40' },
          '& .active>div>span': { color: '#1D1929' },
          '& .MuiMenuItem-root>div>div>p': { fontSize: '14px' },
          '& .MuiMenuItem-root>div>div>span': { fontSize: '12px' },
          '& .MuiMenuItem-root:hover>div': { background: '#77757F1A', color: '#1D1929' },
          '& .MuiMenuItem-root:hover>div>div>span': { color: '#1D1929' },
          '& .active>div>span.emt-icons': { display: 'none' },
        },
      }}
      value={appModeValue}
      options={appOptions}
      iconClass="emt"
      disableUnderline
      variant="filled"
      onChange={(val) => {
        const url =
          val === APP_MODE.SITTER
            ? `https://app.supersitters.app/?token=${encodeURIComponent(
                localStorage.getItem(TokenKeys.Auth ?? '') || '',
              )}&refreshToken=${encodeURIComponent(localStorage.getItem(TokenKeys.Refresh ?? '') || '')}&type=borrower`
            : // `https://app.empruntemontoutou.com/?token=${encodeURIComponent(
              //     localStorage.getItem(TokenKeys.Auth ?? '') || '',
              //   )}&refreshToken=${encodeURIComponent(
              //     localStorage.getItem(TokenKeys.Refresh ?? '') || '',
              //   )}&type=borrower`
              null

        url && openUrlApp(url)

        router.push(appendQueryType(Routes.Home))
      }}
      renderValue={(val) => {
        const found = appOptions.find((o) => o.value === APP_MODE.EMT)
        if (!found) return null

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ borderRadius: '12px', overflow: 'hidden' }}>
              {React.isValidElement(found.media) ? found.media : null}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
              <span style={{ fontWeight: 700 }}>{found.label}</span>
              <span style={{ fontSize: 12, fontWeight: 400 }}>{found.caption}</span>
            </Box>
          </Box>
        )
      }}
    />
  )
}
